"use client";

import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useProduct } from "@/hooks/api/useProduct";
import { useStockMovements } from "@/hooks/api/useStockMovements";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiBaseURL } from "@/lib/api-config";
import { cn } from "@/lib/utils";

export default function ProductDetailPage() {
  const params = useParams();
  const id = Number(params.id);
  const productQ = useProduct(Number.isFinite(id) ? id : null);
  const movQ = useStockMovements(Number.isFinite(id) ? id : null);
  const p = productQ.data;

  if (!Number.isFinite(id)) {
    return <p className="text-sm text-destructive">Invalid id</p>;
  }

  if (productQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  if (productQ.isError || !p) {
    return <p className="text-sm text-destructive">Product not found.</p>;
  }

  const img =
    p.image_url &&
    (p.image_url.startsWith("http") ? p.image_url : `${apiBaseURL}${p.image_url}`);

  return (
    <div className="space-y-6">
      <Link
        to="/products"
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-1")}
      >
        <ArrowLeft className="h-4 w-4" />
        Back to products
      </Link>
      <div className="flex flex-col gap-6 lg:flex-row">
        <Card className="retail-card-hover max-w-sm flex-1">
          <CardHeader>
            <CardTitle>{p.name}</CardTitle>
            <p className="font-mono text-xs text-muted-foreground">{p.sku}</p>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {img ? (
              <img src={img} alt="" className="max-h-48 rounded-md border object-contain" />
            ) : null}
            <p>
              <span className="text-muted-foreground">Category:</span>{" "}
              {p.category_name ?? "—"}
            </p>
            <p>
              <span className="text-muted-foreground">Unit:</span>{" "}
              {p.unit_name ? `${p.unit_name} (${p.unit_short_name})` : "—"}
            </p>
            <p className="font-mono">
              Stock: {Number(p.stock_qty).toFixed(3)}
            </p>
            <p className="font-mono">
              Sell ₹{Number(p.selling_price).toFixed(2)} · MRP ₹{Number(p.mrp).toFixed(2)} · GST{" "}
              {Number(p.gst_rate).toFixed(2)}%
            </p>
          </CardContent>
        </Card>
        <Card className="flex-[2]">
          <CardHeader>
            <CardTitle className="text-base">Stock movements</CardTitle>
          </CardHeader>
          <CardContent>
            {movQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading history…</p>
            ) : (
              <div className="max-h-[480px] overflow-auto rounded-md border">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-muted/80">
                    <tr>
                      <th className="p-2">When</th>
                      <th className="p-2">Type</th>
                      <th className="p-2 text-right">Qty</th>
                      <th className="p-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(movQ.data ?? []).map((m) => (
                      <tr key={m.id} className="border-t">
                        <td className="p-2 font-mono whitespace-nowrap">{m.created_at}</td>
                        <td className="p-2">{m.movement_type}</td>
                        <td className="p-2 text-right font-mono">
                          {Number(m.qty_change).toFixed(3)}
                        </td>
                        <td className="p-2 text-muted-foreground">{m.reason ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(movQ.data ?? []).length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No movements yet.</p>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
